import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Check, Clock, IndianRupee, MapPin, Phone, Ticket } from "lucide-react";
import { usePageTitle } from "@/lib/usePageTitle";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { useRequireAuth } from "@/lib/useRequireAuth";
import { localized } from "@/theme/tenantTheme";
import { resolveImage } from "@/lib/media";
import { ShareButton } from "@/components/ShareButton";
import { FavoriteButton } from "@/components/FavoriteButton";
import { Button } from "@/components/ui/button";
import { whenText } from "@/components/EventCard";
import { rupees } from "@/lib/format";
import { displayPhone, telHref } from "@/lib/phone";

/** The website is where a shared event opens: read-only, crawlable, with the app as the next step. */
export default function EventDetail() {
  const { id } = useParams();
  const { t, lang, auth } = useApp();
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const [event, setEvent] = useState(null);
  const [missing, setMissing] = useState(false);
  const [going, setGoing] = useState(false);
  const [busy, setBusy] = useState(false);
  usePageTitle(event ? localized(lang, event, "title") : "");

  useEffect(() => {
    setMissing(false);
    setEvent(null);
    api
      .get(`/city/events/${id}`)
      .then(({ data }) => setEvent(data))
      .catch(() => setMissing(true));
  }, [id]);

  // Signed in: is this event already one of my RSVPs? (Signed out, the button just asks to sign in.)
  useEffect(() => {
    setGoing(false);
    if (!auth) return undefined;
    let alive = true;
    api
      .get("/event-rsvps/mine")
      .then((r) => alive && setGoing(rows(r.data).some((x) => x.event_id === id)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [id, auth]);

  const rsvp = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      await api.post(`/city/events/${id}/rsvp`);
      setGoing(true);
      toast.success(t("community.rsvpSaved"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (missing)
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="event-not-found">
        <p className="text-lg">{t("events.notFound")}</p>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/")} data-testid="event-back-btn">
          {t("events.backToList")}
        </Button>
      </div>
    );
  if (!event) return <div className="px-5 py-24 text-center text-muted-foreground">{t("common.loading")}</div>;

  const title = localized(lang, event, "title");
  const facts = [
    { icon: CalendarDays, value: whenText(event, lang) },
    { icon: Clock, value: event.start_time_local },
    { icon: MapPin, value: localized(lang, event, "venue") },
    {
      icon: event.entry_type === "free" ? Ticket : IndianRupee,
      value: event.entry_type === "free" ? t("events.free") : rupees({ minor_units: event.entry_fee?.minor_units }),
    },
  ].filter((fact) => fact.value);

  return (
    <div data-testid="event-detail-page">
      {event.cover_image_url && (
        <div className="relative h-[38vh] min-h-[260px] w-full overflow-hidden bg-muted">
          <img src={resolveImage(event.cover_image_url)} alt={title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        </div>
      )}

      <div className="mx-auto max-w-4xl px-5 py-10 sm:px-8">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="mb-5 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          data-testid="event-home-link"
        >
          <ArrowLeft size={14} /> {t("events.backToList")}
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="event-title">
            {title}
          </h1>
          <div className="flex items-center gap-2">
            <FavoriteButton targetType="event" targetId={event.id} />
            <ShareButton path={`/events/${event.id}`} title={title} testId="event-share-btn" />
          </div>
        </div>

        {event.organiser_name && (
          <p className="mt-3 text-base text-muted-foreground" data-testid="event-organiser">
            {event.organiser_name}
          </p>
        )}

        <div className="mt-8 flex flex-wrap gap-3" data-testid="event-facts">
          {facts.map((fact) => (
            <span
              key={fact.value}
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm"
            >
              <fact.icon size={14} className="text-[hsl(var(--primary))]" /> {fact.value}
            </span>
          ))}
        </div>

        <p
          className="mt-10 whitespace-pre-line text-base leading-relaxed text-muted-foreground"
          data-testid="event-description"
        >
          {localized(lang, event, "description")}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={rsvp}
            disabled={busy || going}
            className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-80 disabled:hover:translate-y-0"
            data-testid="event-rsvp-btn"
          >
            {going ? (
              <>
                <Check size={15} /> {t("community.rsvped")}
              </>
            ) : (
              t("community.rsvp")
            )}
          </button>
          {telHref(event.contact_phone) && (
            <a
              href={telHref(event.contact_phone)}
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              data-testid="event-call-btn"
            >
              <Phone size={15} /> {displayPhone(event.contact_phone)}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
